import React, { useCallback, useEffect } from 'react';
import { Button, message, Modal, Spin } from 'antd';
import s from './s.module.less';
import { KlarityService, ServerKlarityService, ServerMeta, TLeadGenFeeConfig, TLeadGenFeeInfo } from 'types/common';
import ConfigService from 'components/ApplicationFormComponents/KlarityServices/components/ConfigService';
import { UniprofileFormData } from 'types/provider';
import { IInsurance } from 'types/insurance';
import { ServerData } from 'components/ApplicationFormComponents/KlarityServices/types';
import { getLeadGenFee } from 'api/klarityService';
import LeadGenFeeTable from 'components/LeadGenFeeTable';
import useInsuranceBillingStatus from 'hooks/useInsuranceBillingStatus';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
// import useGetPPAServicePriceConfig from 'hooks/useGetPPAServicePriceConfig';
// import { SettingEnum } from 'types/enumerationData';

type TSpecialtySaveData = {
    providerKlarityService: ServerKlarityService[];
    disableIds: number[];
    conditions: string[];
};
interface Step3ServiceConfigurationProps {
    isEdit: boolean;
    editingValue?: ServerKlarityService[];
    provider: UniprofileFormData;
    uniprofileInsuranceData?: IInsurance[];
    onNext: (saveData: TSpecialtySaveData) => void;
    onBack: () => void;
    selectedSpecialties: KlarityService[];
    templateData: {
        currentSpecialtyIndex: number;
        configuredServices: { [specialtyId: number]: unknown[] };
    };
    onServiceConfigurationChange: (specialtyId: number, services: unknown[]) => void;
    serverIdStateMap: Record<string, ServerMeta[]>;
}

const Step3ServiceConfiguration: React.FC<Step3ServiceConfigurationProps> = ({
    isEdit,
    provider,
    editingValue,
    uniprofileInsuranceData,
    onNext,
    onBack,
    selectedSpecialties,
    templateData,
    onServiceConfigurationChange,
    serverIdStateMap,
}) => {
    const { currentSpecialtyIndex } = templateData;
    // const [servicePriceConfig, priceConfigLoading] = useGetPPAServicePriceConfig();
    const [isEditingConditions, setIsEditingConditions] = React.useState(false);
    const [, setHasNameError] = React.useState<boolean>(false);
    const [pendingSaveData, setPendingSaveData] = React.useState<ServerData>();
    const currentSpecialty = selectedSpecialties[currentSpecialtyIndex - 1];
    // const [currentSettingServiceTypeInx] = React.useState<number>(1);
    const [loading, setLoading] = React.useState(false);
    const [insuranceBillingStatus] = useInsuranceBillingStatus();
    const [statesAndFeeMap, setStatesAndFeeMap] = React.useState<{ states: string[]; fee: number }[]>([]);
    const [showLeadGenFeeModal, setShowLeadGenFeeModal] = React.useState(false);
    const [submitLoading, setSubmitLoading] = React.useState(false);
    const [homeInfo] = useProviderHomeInfo();
    // 只有真正的 PPV（后端 ppvProvider）才算 PPV；没有 vendor mapping 的 EHR Only / billing 起步用户不能再按「非 PPA」当成 PPV
    const isPPV = !!homeInfo?.ppvProvider;
    // UI有时候渲染的时间有点久
    useEffect(() => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
        }, 1000);
    }, []);

    const addressList = React.useMemo(() => {
        return provider?.practice?.addressList || [];
    }, [provider?.practice?.addressList]);

    const handleNext = async (submit?: boolean) => {
        if (submit) {
            setSubmitLoading(true);
            onNext?.(pendingSaveData);
            return;
        }
        const services = pendingSaveData?.providerKlarityService || [];
        for (let i = 0; i < services.length; i++) {
            const cpts = services[i]?.cpts;
            if (cpts && cpts.length > 0) {
                const hasIncomplete = cpts.some((cpt) => !cpt.description?.trim() || !cpt.insuranceRate);
                if (hasIncomplete) {
                    message.error('Please fill in Description and Insurance rate for all billing codes');
                    return;
                }
            }
        }
        if (insuranceBillingStatus?.onboardingCompleted) {
            for (let i = 0; i < services.length; i++) {
                const cpts = services[i]?.cpts;
                const hasCompleteCpt = cpts?.some(
                    (cpt) => cpt.cptCode?.trim() && cpt.description?.trim() && cpt.insuranceRate,
                );
                if (!hasCompleteCpt && !isPPV) {
                    message.error(
                        'At least one complete billing code with Insurance rate is required for each service.',
                    );
                    return;
                }
            }
        }
        const allStates: string[] = [];
        pendingSaveData?.providerKlarityService?.forEach((service) => {
            service.serviceStateItems?.forEach((stateItem) => {
                if (!allStates.includes(stateItem.state)) {
                    allStates.push(stateItem.state);
                }
            });
        });
        let hasNewAddState = false;
        if (!editingValue) {
            hasNewAddState = true;
        } else {
            const allPreStates: string[] = [];
            editingValue?.forEach((service) => {
                service.serviceStateItems?.forEach((stateItem) => {
                    if (!allPreStates.includes(stateItem.state)) {
                        allPreStates.push(stateItem.state);
                    }
                });
            });
            const allNewStates: string[] = [];
            pendingSaveData?.providerKlarityService?.forEach((service) => {
                service.serviceStateItems?.forEach((stateItem) => {
                    if (!allNewStates.includes(stateItem.state)) {
                        allNewStates.push(stateItem.state);
                    }
                });
            });
            hasNewAddState = allNewStates.some((state) => !allPreStates.includes(state));
        }
        const isPPA = homeInfo?.ppaProvider;
        const showLenGenFeeUpdate = isPPA && hasNewAddState;

        //https://applink.larksuite.com/client/message/link/open?token=AmkFQppW2E20aoeeLClAEHY%3D
        setSubmitLoading(false);
        onNext?.(pendingSaveData);
        return;

        // if (!showLenGenFeeUpdate) {
        //     setSubmitLoading(false);
        //     onNext?.(pendingSaveData);
        //     return;
        // }
        const feeRes = await getLeadGenFee({
            specialty: currentSpecialty?.specialtyName || '',
            states: allStates,
        });
        if (!feeRes.error) {
            const configs: TLeadGenFeeConfig[] = feeRes.data?.data;
            if (configs && configs.length > 0) {
                const _statesAndFeeMap: { states: string[]; fee: number }[] = [];
                allStates.forEach((state) => {
                    const targetConfig = configs.find((config) => config.state === state);
                    if (targetConfig) {
                        const fee = targetConfig.leadGenFee;
                        const targetItem = _statesAndFeeMap.find((item) => item.fee === fee);
                        if (targetItem) {
                            targetItem.states.push(state);
                        } else {
                            _statesAndFeeMap.push({
                                states: [state],
                                fee,
                            });
                        }
                    }
                });
                setStatesAndFeeMap(_statesAndFeeMap.sort((a, b) => a.fee - b.fee));
                if (_statesAndFeeMap.length > 0) {
                    setShowLeadGenFeeModal(true);
                } else {
                    setSubmitLoading(false);
                    onNext?.(pendingSaveData);
                }
            }
        }
    };

    return (
        <div className={s.wrap}>
            <Spin spinning={loading}>
                {/* 页面上部文字 */}
                <div className={s.header}>
                    <h1 className={s.title}>
                        Set up your {currentSpecialty?.specialtyName || 'your selected specialty'} services
                    </h1>
                    <p className={s.subtitle}>
                        Choose your service states and add the service name, description, payment method, duration, and
                        other details to help patients understand what to expect.
                    </p>
                </div>
                <ConfigService
                    isEditingConditions={isEditingConditions}
                    setIsEditingConditions={setIsEditingConditions}
                    provider={provider}
                    uniprofileInsuranceData={uniprofileInsuranceData}
                    isKiwiUser
                    isEdit={isEdit}
                    onHasNameErrorUpdate={setHasNameError}
                    onUpdate={setPendingSaveData}
                    serviceDatas={selectedSpecialties}
                    editingValue={editingValue}
                    serverIdStateMap={serverIdStateMap}
                    currentEditingStep={currentSpecialtyIndex}
                    providerId={provider?.id}
                    addressList={addressList}
                    forceEnableStateSelect
                    // servicePriceConfig={servicePriceConfig}
                    useInsuranceComponent
                    hideAddNew={!isEdit}
                    forceShowProcess={false}
                    // allStatesFromService={[]}
                />

                {/* 底部按钮 */}
                <div className={s.footer}>
                    <Button onClick={onBack}>Back</Button>
                    <Button
                        type="primary"
                        onClick={() => {
                            console.log('click 2');
                            handleNext();
                        }}
                        disabled={selectedSpecialties?.length === 0}
                    >
                        {currentSpecialtyIndex < (selectedSpecialties?.length || 0) ? 'Save' : 'Complete Setup'}
                    </Button>
                </div>
            </Spin>
            <Modal
                open={showLeadGenFeeModal}
                onCancel={() => {
                    setShowLeadGenFeeModal(false);
                    setSubmitLoading(false);
                }}
                footer={null}
                width={550}
                title="Review your lead-generation fee setup"
            >
                <div className={s.mWrap}>
                    <div className={s.mTips}>
                        Lead-gen fees vary by state and service type. Fees are charged 24 hours after a new patient
                        booking is confirmed. No fee applies if the patient cancels within 24 hours.
                    </div>
                    <LeadGenFeeTable statesAndFeeMap={statesAndFeeMap} />
                    <div className={s.mButtons}>
                        <Button
                            loading={submitLoading}
                            type="primary"
                            block
                            size="large"
                            onClick={() => {
                                console.log('click');
                                handleNext(true);
                            }}
                        >
                            Confirm
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Step3ServiceConfiguration;
